import { useState } from 'react';

import {
  useApiKeys,
  type ApiKeyAudience,
  type ApiKeyResponse,
  type CreateApiKeyResponse,
} from '../../hooks/use-api-keys';
import { getCachedConfig } from '../../lib/runtime-config';

import { AudienceBadge } from './AudienceBadge';
import { EditKeyModal } from './EditKeyModal';
import { MintKeyModal } from './MintKeyModal';
import { NewKeyReveal } from './NewKeyReveal';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Short label for what a key acts as, shown next to the name. */
function actsAsLabel(k: { acts_as: string; agent_id: string | null; role_id: string | null }): string {
  if (k.acts_as === 'agent') return 'agent';
  if (k.acts_as === 'role') return 'role-only';
  return 'me';
}

export function ApiKeysView() {
  const { keys, status, error, subtree, refresh, createKey, updateKey, revokeKey } = useApiKeys();
  const [mintAudience, setMintAudience] = useState<ApiKeyAudience | null>(null);
  const [reveal, setReveal] = useState<CreateApiKeyResponse | null>(null);
  const [editing, setEditing] = useState<ApiKeyResponse | null>(null);

  const mcpKeys = keys.filter((k) => k.audience === 'mcp');
  const directKeys = keys.filter((k) => k.audience === 'direct_api');
  const oaiKeys = keys.filter((k) => k.audience === 'openai_completions');
  const integrationKeys = keys.filter((k) => k.audience === 'integration');
  const orgCount = subtree ? new Set(keys.map((k) => k.tenant?.id ?? '')).size : 0;

  return (
    <div data-testid="api-keys-view" style={{ height: '100%', overflowY: 'auto', background: 'var(--bg)' }}>
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          zIndex: 1,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>API Keys</div>
        <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          {keys.length} {keys.length === 1 ? 'key' : 'keys'}
          {keys.length
            ? ` (${mcpKeys.length} MCP, ${directKeys.length} direct, ${oaiKeys.length} OpenAI${integrationKeys.length ? `, ${integrationKeys.length} integration` : ''})`
            : ''}
          {subtree && keys.length ? ` across ${orgCount} ${orgCount === 1 ? 'org' : 'orgs'}` : ''}
        </div>
        <div style={{ flex: 1 }} />
        <button
          data-testid="mint-key-button"
          onClick={() => setMintAudience('mcp')}
          style={{
            padding: '6px 12px',
            background: 'var(--accent)',
            color: '#fff',
            border: 0,
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          + New API key
        </button>
      </div>

      <div style={{ padding: 20 }}>
        {status === 'loading' && keys.length === 0 ? (
          <div style={{ color: 'var(--text-soft)', fontSize: 13 }}>Loading…</div>
        ) : status === 'error' ? (
          <div style={{ color: '#c0392b', fontSize: 13 }}>
            {error}
            <button
              onClick={() => void refresh()}
              style={{ marginLeft: 12, padding: '2px 8px', fontSize: 11 }}
            >
              Retry
            </button>
          </div>
        ) : keys.length === 0 ? (
          <div
            data-testid="api-keys-empty"
            style={{
              padding: '60px 24px',
              textAlign: 'center',
              color: 'var(--text-soft)',
              fontSize: 13,
            }}
          >
            <div style={{ fontSize: 16, marginBottom: 8, color: 'var(--text)' }}>No API keys yet</div>
            <div>Connect via MCP to wire up Claude Desktop, or generate a direct API key for scripts and integrations.</div>
          </div>
        ) : (
          <table
            data-testid="api-keys-table"
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
          >
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-soft)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <th style={{ padding: '8px 6px' }}>Name</th>
                {subtree && <th style={{ padding: '8px 6px' }}>Org</th>}
                <th style={{ padding: '8px 6px' }}>Audience</th>
                <th style={{ padding: '8px 6px' }}>Prefix</th>
                <th style={{ padding: '8px 6px' }}>Scopes</th>
                <th style={{ padding: '8px 6px' }}>Last used</th>
                <th style={{ padding: '8px 6px' }}>Status</th>
                <th style={{ padding: '8px 6px' }}></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const revoked = Boolean(k.revoked_at);
                return (
                  <tr
                    key={k.id}
                    data-testid={`api-key-row-${k.id}`}
                    style={{ borderTop: '1px solid var(--border)', opacity: revoked ? 0.55 : 1 }}
                  >
                    <td style={{ padding: '10px 6px', color: 'var(--text)' }}>
                      {k.name ?? '—'}
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-soft)' }}>
                        · {actsAsLabel(k)}
                      </span>
                    </td>
                    {subtree && (
                      <td
                        style={{ padding: '10px 6px', color: 'var(--text-soft)', fontSize: 12 }}
                        title={k.tenant?.path ?? ''}
                      >
                        {k.tenant?.name ?? '—'}
                      </td>
                    )}
                    <td style={{ padding: '10px 6px' }}><AudienceBadge audience={k.audience} /></td>
                    <td style={{ padding: '10px 6px', fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--text-soft)' }}>
                      {k.key_prefix}…{k.key_hint}
                    </td>
                    <td style={{ padding: '10px 6px', color: 'var(--text-soft)', fontSize: 12 }}>
                      {k.scopes.length === 0 ? '—' : k.scopes.join(', ')}
                    </td>
                    <td style={{ padding: '10px 6px', color: 'var(--text-soft)' }}>
                      {fmtDate(k.last_used_at)}
                    </td>
                    <td style={{ padding: '10px 6px' }}>
                      {revoked ? (
                        <span style={{ color: '#c0392b', fontSize: 12 }}>Revoked</span>
                      ) : (
                        <span style={{ color: '#2f855a', fontSize: 12 }}>Active</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {!revoked && (
                        <>
                          <button
                            data-testid={`edit-api-key-${k.id}`}
                            onClick={() => setEditing(k)}
                            style={{
                              padding: '2px 8px',
                              fontSize: 11,
                              background: 'transparent',
                              border: '1px solid var(--border)',
                              borderRadius: 4,
                              cursor: 'pointer',
                              color: 'var(--text-soft)',
                              marginRight: 6,
                            }}
                          >
                            Edit
                          </button>
                          <button
                            data-testid={`revoke-api-key-${k.id}`}
                            onClick={() => {
                              if (confirm(`Revoke "${k.name}"? Cannot be undone.`)) void revokeKey(k.id);
                            }}
                            style={{
                              padding: '2px 8px',
                              fontSize: 11,
                              background: 'transparent',
                              border: '1px solid var(--border)',
                              borderRadius: 4,
                              cursor: 'pointer',
                              color: 'var(--text-soft)',
                            }}
                          >
                            Revoke
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {mintAudience && (
        <MintKeyModal
          audience={mintAudience}
          onClose={() => setMintAudience(null)}
          onMinted={(result) => {
            setMintAudience(null);
            setReveal(result);
          }}
          createKey={createKey}
        />
      )}

      {editing && (
        <EditKeyModal
          apiKey={editing}
          onClose={() => setEditing(null)}
          updateKey={updateKey}
        />
      )}

      {reveal && (
        <NewKeyReveal
          result={reveal}
          mcpPublicUrl={getCachedConfig()?.mcp_url ?? ''}
          apiBaseUrl={getCachedConfig()?.api_base_url ?? ''}
          onClose={() => setReveal(null)}
        />
      )}
    </div>
  );
}
